import Vector from "ol/source/Vector";

import { beginOfBudgetYears, createDate } from "@/common/utils";
import { IMapLayerSource, IMapLayer } from "@/components/map/types";
import { EEcoPlantType, makeCropLayer, 
         makeCropOverallLayer, makeCropStageLayer, 
         MONTHS, LEGEND08, LEGEND24, LEGEND_STAGE 
       } from "./ecoplant";

import { APP_URL } from "@/common/http";

const BUDGET_YEARS = beginOfBudgetYears();
const MAX_RESOLUTION_4_EDITING = 4.777314267158508 / 2;
const TODAY_ISO = (new Date()).toISOString().substr(0,10);

export function createUserLayer(wmsWmts) : IMapLayer {
  //called from ../toc/map.layers.vue
  let ms:IMapLayerSource[] = [];
  let svc = wmsWmts.serviceType.toUpperCase();
  let n = wmsWmts.serviceUrl.indexOf('?');
  if (n == (wmsWmts.serviceUrl.length-1)) {
    wmsWmts.serviceUrl = wmsWmts.serviceUrl.substr(0,n);
  }
  wmsWmts.geomType = wmsWmts.geomType.capitalize();
  let style;
  if (wmsWmts.geomType=='Point') {
    style = {
      border: '2px solid #000000',
      'max-width': '16px!important',
      'min-width': '16px!important',
      height: '16px!important',
      'margin-left': '2px!important',
      'margin-right': '10px!important',
      'border-radius': '50%'
    };
  } else if (wmsWmts.geomType=='Line') {
    style = undefined;
  } else {
    style = {
      border: '2px solid #666666'
    };
  }
  if (svc=='WMS') {
    ms = [{
      service: svc,
      serverType: 'geoserver',
      url: wmsWmts.serviceUrl,
      params: Object.assign({
        'LAYERS': wmsWmts.layerName, 
        'TILED': true
      },wmsWmts.params || {}),
      visible: wmsWmts.visible
    }]
  } else if (svc=='WMTS') {
    ms = [{
      service: svc,
      wmtsMatrixSet: wmsWmts.wmtsMatrixSet,
      wmtsEPSG: wmsWmts.wmtsEpsg,
      url: wmsWmts.serviceUrl,
      serverType: 'geoserver',
      featureType: wmsWmts.layerName,
      visible: wmsWmts.visible
    }];
  }
  if (wmsWmts.userName && wmsWmts.userPassword) {
    ms[0].authen = {
      username: wmsWmts.userName,
      password: wmsWmts.userPassword
    }
  }
  return {
    id: wmsWmts.serviceId,
    title: wmsWmts.serviceName,
    type: 'checkbox',
    checked: wmsWmts.visible,
    visible: wmsWmts.visible,
    geomType: wmsWmts.geomType,
    mapSources: ms,
    doAction: MAP_LAYERS[0].children[0].doAction || undefined,
    style: style,
    legend: [{ //todo : wms.getLegendGraphic
        title: '',
        style: {
          'min-width': '190px',
          'max-width': '190px'
        },
        image: 'https://gisagro.gistda.or.th/rest/gis/wms?service=WMS&request=GetLegendGraphic&format=image/png&width=20&height=20&layer=gisagro:national_forest'
      }],
    image: wmsWmts.geomType=='Line' ? '/static/images/legend-boundary-amphoe.svg' : undefined
  };
}

export function makeExternalWmsWmtsLayers(wms:any) {
  try {
    if (wms) {
      let layer:IMapLayer;
      let points:IMapLayer[] = [];
      let lines:IMapLayer[] = [];
      let polygons:IMapLayer[] = [];
      wms.forEach((d) => {
        layer = createUserLayer({
          serviceId: d.idx_rn,
          serviceName: d.service_name,
          serviceType: d.service_type,
          serviceUrl: d.service_url,
          layerName: d.layer_name,
          geomType: d.geom_type,
          wmtsMatrixSet: d.wmts_matrixset,
          wmtsEpsg: d.wmts_epsg,
          authKey: d.access_token,
          userName: d.user_name,
          userPassword: d.user_password
        });
        if (layer.geomType=='Point') {
          points.push(layer);
        } else if (layer.geomType=='Line') {
          lines.push(layer);
        } else {
          polygons.push(layer);
        }
      });
      MAP_LAYERS.push({
        header: 'ข้อมูลบริการอื่นๆ',
        children: points.concat(lines).concat(polygons)
      });
    }
  } catch (error) {}
  MAP_LAYERS.push({
    header: 'ข้อมูลของผู้ใช้',
    children: []
  });
}

export function makeEcoplantLayers(ds:{[key:string]:any}) {
  let layers = ['overall','stage'].map((layerType) => {
    if (layerType=='overall') {
      return [ 'rice','maize','sugarcane','cassava'].map((crop) => {
        return makeCropOverallLayer(<EEcoPlantType>crop, ds[0], 1, false);
      });
    } else {
      return [ 'rice','maize','sugarcane','cassava'].map((crop) => {
        return makeCropStageLayer(<EEcoPlantType>crop, ds[0], 1, false);
      });
    }
  });
  layers.forEach((layer, index) => {
    MAP_LAYERS[0].children[++index].children = layer;
  });
}

export function setFloodYears(years:any[]) {
  MAP_LAYERS[2].children[0].children[1].actions[0].selected = years[0].year + '';
  MAP_LAYERS[2].children[0].children[1].actions[0].options = years.map((option) => {
    return {
        title: (option.year + 543) + '',
        value: option.year + ''
    };
  });
}

export const Weather3HrsStyle = (feature, resolution) => {
  return {
    points:[{
      icon: {
        src: APP_URL + '/static/images/tmd/' + (feature ? feature.get('icon') : 'sunny.gif'),
        size: [20,20],
        offset: [10,10],
        scale: resolution>=2000 ? 0.5 : resolution>=1000 ? 0.7 : resolution>=500 ? 0.85 : 1.0,
        opacity: 0.8,
        rotateWithView: true
      }
    }]
  };
}

const createAgritronicsStationStyle = (color:string,count:number) => {
  return {
    label: { 
      text: count + '', 
      fill: { color: 'rgb(255,255,255)'},
      rotation: 0 
    },
    points:[{
      circle: {
        radius: 12,
        fill: { color: color }
      }
    }]
  };
};

export const AgritronicsStationStyle = (feature, resolution) => {
  let howMany = feature ? feature.get('features').length : 0;
  return createAgritronicsStationStyle('rgba(214,40,40,0.85)',howMany);
}

export const AgritronicsStationPhase2Style = (feature, resolution) => {
  let howMany = feature ? feature.get('features').length : 0;
  return createAgritronicsStationStyle('rgba(32,160,32,0.85)',howMany);
}

export const WaterStationStyle = (feature, resolution) => {
  let howMany = feature ? feature.get('features').length : 0;
  return createAgritronicsStationStyle('rgba(32,150,242,0.85)', howMany);
}

//these features need to be replaced in print page 
//since their style are function that cannot be JSON.stringified()
export const FEATURES : {[key:string] : IMapLayerSource} = {
    Weather3Hrs: {
      id: 'replaceWeather3Hrs',
      service: 'WFS',
      serverType: 'geoserver',
      featureType: 'gisagro:weather_3hrs',
      geometryType: 'Point',
      strategy: '', //always ue bbox
      //maxResolution: MAX_RESOLUTION_4_EDITING * 4,
      visible: true, //display it after added to map
      style: Weather3HrsStyle,
      zIndexOffset: 999
    },
    Agritronics: {
      id: 'replaceAgritronics',
      service: 'Vector',
      clusterDistance: 32,
      style: AgritronicsStationStyle
    },
    AgritronicsPhase2: {
      id: 'replaceAgritronicsPhase2',
      service: 'Vector',
      clusterDistance: 32,
      style: AgritronicsStationPhase2Style
    },
    WaterStation: {
      id: 'replaceWaterStation',
      service: 'Vector',
      clusterDistance: 32,
      style: WaterStationStyle
    }
  }

export const Agritronics:IMapLayerSource = FEATURES.Agritronics;
export const AgritronicsPhase2:IMapLayerSource = FEATURES.AgritronicsPhase2;
export const WaterStation:IMapLayerSource = FEATURES.WaterStation;

const MAP_SOURCES: {[key:string] : IMapLayerSource } = {
  wms_farmland_fnr: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:farmland_fnr', 
      'TILED': true
    },
    filterOptions: ['aag','crop','deed','year','area'],
    visible: false
  },
  wms_farmland_frd: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:farmland_frd', 
      'TILED': true
    },
    filterOptions: ['aag','crop','deed','year','area'],
    visible: false
  },
  wms_wildlife_sanctuary: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:wildlife_sanctuary', 
      'TILED': true
    },
    useGwc: true, 
    visible: false
  },
  wms_non_hunting: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:non_hunting', 
      'TILED': true
    },
    useGwc: true, 
    visible: false
  },
  wms_national_forest: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:national_forest', 
      'TILED': true
    },
    useGwc: true, 
    visible: false
  },
  wms_forest_park: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:forest_park', 
      'TILED': true
    },
    useGwc: true, 
    visible: false
  },
  wms_national_park: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:national_park', 
      'TILED': true
    },
    useGwc: true, 
    visible: false
  },
  wms_mangrove_forest: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:mangrove_forest', 
      'TILED': true
    },
    useGwc: true, 
    visible: false
  },
  wms_alro: {
    service: 'WMS',
    serverType: 'geoserver',
    params: {
      'LAYERS': 'gisagro:alro_area', 
      'TILED': true
    },
    useGwc: true, 
    visible: false
  }
}
const SUITABILITY_LEGEND = [{
    title: "เหมาะสมมาก",
    style: {
      background:'#006600' //'#686868' //'#006400'
    }
  },{
    title: "เหมาะสมปานกลาง",
    style: {
      background:'#32cd32' //'#9E9E9E' //'#66CC00'
    }
  },{
    title: "เหมาะสมน้อย",
    style: {
      background:'#ff8c00' //'#DCDCDC' //'#FF9933'
    }
  },{
    title: "ไม่เหมาะสม",
    style: {
      background:'#b22222' //'#CC3300' //'#CC3300'
    }
  }];
export const MAP_LAYERS: IMapLayer[] = [{
    header: 'ข้อมูลประกอบเฉพาะพืช',
    children: [{
        title: 'พื้นที่เพาะปลูกรายแปลง',
        type: 'checkbox',
        checked: false, // IMPORTANT: must defined, otherwise, the UI wont refresh when value changed
        visible: false,
        geomType: 'Polygon',
        mapSources: [
          //dont change following order, it displays fnr under frd
          MAP_SOURCES.wms_farmland_fnr,
          MAP_SOURCES.wms_farmland_frd
          ],
        image: '/static/images/legend-line.svg',
        legend: [{
            title: 'แปลงที่ระบุชื่อเกษตรกร',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources:[
                MAP_SOURCES.wms_farmland_frd
              ],
            style: {
              border: '1px solid #33cc00'
            }
          },{
            title: 'แปลงที่ไม่ระบุชื่อเกษตรกร',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources:[
                MAP_SOURCES.wms_farmland_fnr
              ],
            style: {
              border : '1px solid #ff0000'
            }
          }]
      },{
        title: 'พื้นที่ปลูกพืชเศรษฐกิจจากดาวเทียม',
        image: '/static/images/legend-polygon.svg',
        children: null
      },{
        title: 'การเจริญเติบโตของพืชเศรษฐกิจในปัจจุบัน',
        mapSources: [], // in order to hold all layers from children
        image: '/static/images/legend-polygon.svg',
        children: null
      },/*{
        title: 'พื้นที่ปลูกข้าวจากดาวเทียม',
        image: '/static/images/legend-polygon.svg',
        mapSources: [], // in order to hold all layers from children
        children: null // to be created from makeEcoplantLayers90
      },{
        title: 'พื้นที่ปลูกข้าวโพดจากดาวเทียม',
        image: '/static/images/legend-polygon.svg',
        mapSources: [], // in order to hold all layers from children
        children: null // to be created from makeEcoplantLayers90
      },{
        title: 'พื้นที่ปลูกอ้อยจากดาวเทียม',
        image: '/static/images/legend-polygon.svg',
        mapSources: [], // in order to hold all layers from children
        children: null // to be created from makeEcoplantLayers90
      },{
        title: 'พื้นที่ปลูกมันสําปะหลังจากดาวเทียม',
        image: '/static/images/legend-polygon.svg',
        mapSources: [], // in order to hold all layers from children
        children: null // to be created from makeEcoplantLayers90
      },*/{
        title: 'การใช้ที่ดินทางการเกษตร',
        image: '/static/images/legend-polygon.svg',
        children: [{
            title: 'กาแฟ',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_coffee', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=5002'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background: '#795548',
              border:'1px solid #000'
            }
          },{
            title: 'ข้าว',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_rice', 
                  'TILED': true
                  //,CQL_FILTER': 'crop_fk=1002'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background: '#FFFF66',
              border:'1px solid #000'
            }
          },{
            title: 'ข้าวโพดเลี้ยงสัตว์',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_maize', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=1003'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background: '#FFCC66',
              border:'1px solid #000'
            }
          },{
            title: 'เงาะ',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_rambutan', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=4004'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background: '#9C27B0',
              border:'1px solid #000'
            }
          },{
            title: 'ทุเรียน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_durian', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=4003'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background: '#FF6666',
              border:'1px solid #000'
            }
          },{
            title: 'ปาล์มน้ำมัน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_palm', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=2005'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background: '#996633',
              border:'1px solid #000'
            }
          },{
            title: 'มะพร้าว',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_coconut', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=2006'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background: '#CDDC39',
              border: '1px solid #000'
            }
          },{
            title: 'มังคุด',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_mangosteen', 
                  'TILED': true
                  //'CQL_FILTER': 'crop_fk=4009'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background:'#2196F3',
              border:'1px solid #000'
              }
          },{
            title: 'มันสำปะหลังโรงงาน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_cassava', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=1005'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background:'#CE9357',
              border:'1px solid #000'}
          },{
            title: 'ยางพารา ปี 2561',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_rubber_tree', 
                  'TILED': true
                  ,'CQL_FILTER': 'yyyy=2018'
                },
                //useGwc: true, 
                visible: false
              }],
            style: {
              background:'#336600',
              border:'1px solid #000'}
          },{
            title: 'ยางพารา ปี 2562',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_rubber_tree', 
                  'TILED': true
                  ,'CQL_FILTER': 'yyyy=2019'
                },
                //useGwc: true, 
                visible: false
              }],
            style: {
              background:'#18a080',
              border:'1px solid #000'}
          },{
            title: 'ยางพารา ปี 2563',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_rubber_tree', 
                  'TILED': true
                  ,'CQL_FILTER': 'yyyy=2020'
                },
                //useGwc: true, 
                visible: false
              }],
            style: {
              background:'#2bd996',
              border:'1px solid #000'}
          },{
            title: 'ลำไย',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_longan', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=4007'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background:'#66FFFF',
              border:'1px solid #000'}
          },{
            title: 'สวนผสม',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_garden', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=4099'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background:'#2196F3',
              border:'1px solid #000'}
          },{
            title: 'สับปะรดโรงงาน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_pineapple', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=4001'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background:'#8BC34A',
              border:'1px solid #000'}
          },{
            title: 'อ้อยโรงงาน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:landuse_sugarcane', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=1004'
                },
                useGwc: true, 
                visible: false
              }],
            style: {
              background:'#FF66FF',
              border:'1px solid #000'}
        }]
      },{
        title: 'ความเหมาะสมของดิน',
        image: '/static/images/legend-polygon.svg',
        children: [{
            title: 'กาแฟ',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_coffee', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'ข้าว',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_rice', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'ข้าวโพดเลี้ยงสัตว์',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_maize', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'ปาล์มน้ำมัน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_palm', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'มะพร้าว',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_coconut', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'มันลำปะหลังโรงงาน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_cassava', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'ยางพารา',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_rubber_tree', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'ลำไย',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_longan', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'สับปะรด',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              params: {
                'LAYERS': 'gisagro:suitability_pineapple', 
                'TILED': true
              },
              useGwc: true, 
              visible: false
            }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'อ้อยโรงงาน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_sugarcane', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          },{
            title: 'ทุเรียน เงาะ มังคุด',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:suitability_durian_rambutan_mongostean', 
                  'TILED': true
                },
                useGwc: true, 
                visible: false
              }],
            image: '/static/images/legend-polygon.svg',
            legend: SUITABILITY_LEGEND
          }]
      },{
        title: 'โรงงานและแหล่งรับซื้อ',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Point',
        mapSources: [],
        className: 'legend-point',
        image: '/static/images/legend-polygon.svg',
        legend: [{
            title: 'ข้าว',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:factory_rice', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=1002'
                },
                useGwc: true, 
                visible: false
              }],
            className: 'circle12',
            style: {
              background:'#FFFF66'
            }
          },{
            title: 'ข้าวโพดเลี้ยงสัตว์',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:factory_maize', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=1003'
                },
                useGwc: true, 
                visible: false
              }],
            className: 'circle12',
            style: {
              background:'#FFCC66'
            }
          },{
            title: 'ปาล์มน้ำมัน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:factory_palm', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=2005'
                },
                useGwc: true, 
                visible: false
              }],
            className: 'circle12',
            style: {
              background:'#996633'
            }
          },{
            title: 'มันลำปะหลังโรงงาน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:factory_cassava', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=4001'
                },
                useGwc: true, 
                visible: false
              }],
            className: 'circle12',
            style: {
              background:'#CE9357'
            }
          },{
            title: 'ยางพารา',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:factory_rubber', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=5001'
                },
                useGwc: true, 
                visible: false
              }],
            className: 'circle12',
            style: {
              background:'#336600'
            }
          },{
            title: 'ลำไย',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:factory_longan', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=4007'
                },
                useGwc: true, 
                visible: false
              }],
            className: 'circle12',
            style: {
              background:'#66FFFF'
            }
          },{
            title: 'อ้อยโรงงาน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [{
                service: 'WMS',
                serverType: 'geoserver',
                params: {
                  'LAYERS': 'gisagro:factory_sugarcane', 
                  'TILED': true
                  //,'CQL_FILTER': 'crop_fk=1004'
                },
                useGwc: true, 
                visible: false
              }],
            className: 'circle12',
            style: {
              background:'#FF66FF'
            }
          }]
      }]
  },{
    header: 'ข้อมูลภูมิสารสนเทศพื้นฐาน',
    children: [{
      title: 'ขอบเขตการปกครอง',
      type: 'checkbox',
      checked: true,
      visible: true,
      geomType: 'Line',
      image: '/static/images/legend-boundary.svg',
      mapSources: [{
          service: 'WMS',
          serverType: 'geoserver',
          params: {
            'LAYERS': 'gisagro:admin_boundaries', //workspace_name:layer_name
            'TILED': true
          },
          useGwc: true, 
          visible: true, 
        }],
      legend: [{
          title: 'ประเทศ',
          image: '/static/images/legend-boundary-country.svg'
        },{
          title: 'จังหวัด',
          image: '/static/images/legend-boundary-changwat.svg'
        },{
          title: 'อำเภอ',
          image: '/static/images/legend-boundary-amphoe.svg'
        },{
          title: 'ตำบล',
          image: '/static/images/legend-boundary-tambon.svg'
        },{
          title: 'เส้นชายทะเล',
          image: '/static/images/legend-boundary-shoreline.svg'
        }]
      },{
        title: 'สภาพอากาศราย 3 ชั่วโมง',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Point',
        mapSources: [ FEATURES.Weather3Hrs ],
        image: '/static/images/tmd/partlycloudy.gif'
      },{
        id: 'gistdastations',
        title: 'สถานีตรวจวัดของ สทอภ.',
        className: 'legend-point',
        image: '/static/images/legend-polygon.svg',
        actions: [{
          action: 'showGistdaStation',
          type: 'button',
          width: 'auto',
          icon: 'mdi-chart-bar'
        }],
        children: [{
            id: 'gistda_station_source_1',
            title: 'Agritronics',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [ FEATURES.Agritronics ],
            className: 'circle16',
            style: {
              background: 'rgba(214,40,40,0.85)'
            }
          },{
            id: 'gistda_station_source_2',
            title: 'Agritronics ระยะ 2',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [ FEATURES.AgritronicsPhase2 ],
            className: 'circle16',
            style: {
              background:'rgba(32,160,32,0.85)'
            }
          },{
            id: 'gistda_station_source_3',
            title: 'Water Sensor',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Point',
            mapSources: [ FEATURES.WaterStation ],
            className: 'circle16',
            style: {
              background: 'rgba(32,150,242,0.85)'
            }
          }]
      },
      /*{
        title: 'สถานีตรวจวัดสภาพอากาศและภัยพิบัติ',
        className: 'legend-point',
        image: '/static/images/legend-polygon.svg',
        geomType: 'Point',
        legend: [{
            title: 'สถานีตรวจวัดระยะที่ 1',
            style: {
              width:10,
              height:10,
              background: '#ff0000'
            },
            geomType: 'Point'
          },{
            title: 'สถานีตรวจอากาศพืช',
            type: 'checkbox',
            checked: false,
            className: 'legend-point',
            image: '/static/images/legend-polygon.svg',
            geomType: 'Point',
            legend: [{
                title: 'ข้าว',
                className: 'circle16',
                style: {
                  background:'#FFFF66'
                }
              },{
                title: 'ข้าวโพดเลี้ยงสัตว์',
                className: 'circle16',
                style: {
                  background:'#FFCC66'
                }
              },{
                title: 'เงาะนาสาร',
                className: 'circle16',
                style: {
                  background:'#3399FF'
                }
              },{
                title: 'มะขามหวาน',
                className: 'circle16',
                style: {
                  background:'#999999'
                }
              },{
                title: 'มะพร้าว',
                className: 'circle16',
                style: {
                  background:'#6666FF'
                }
              },{
                title: 'มันลำปะหลังโรงงาน',
                className: 'circle16',
                style: {
                  background:'#FF6666'
                }
              },{
                title: 'อ้อยโรงงาน',
                className: 'circle16',
                style: {
                  background:'#FF66FF'
                }
              },{
                title: 'ปาล์มน้ำมัน',
                className: 'circle16',
                style: {
                  background:'#996633'
                }
              },{
                title: 'ลำไย',
                className: 'circle16',
                style: {
                  background:'#66ffff'
                }
              },{
                title: 'ส้ม',
                className: 'circle16',
                style: {
                  background:'#cc3300'
                }
              },{
                title: 'หน่อไม้ฝรั่ง',
                className: 'circle16',
                style: {
                  background:'#990099'
                }
              },{
                title: 'ยางพารา',
                className: 'circle16',
                style: {
                  background:'#336600'
                }
              }]
          }]
      },{
          title: 'ป่าไม้',
          type: 'checkbox',
          checked: false,
          image: '/static/images/legend-polygon.svg',
          geomType: 'Polygon',
          legend: [{
              title: 'ป่าอนุรักษ์',
              style: {
                border:'1px solid #006600',
                background:'#339900'
              }
            },{
              title: 'ป่าเศรษฐกิจ',
              style: {
                border:'1px solid #006600',
                background:'#ff9900'
              }
            },{
              title: 'ป่าที่เหมาะสมต่อการเกษตร',
              style: {
                border:'1px solid #006600',
                background:'#ccff33'
              }
            }]
      }*/{
          title: 'พื้นที่ ส.ป.ก.',
          type: 'checkbox',
          checked: false,
          visible: false,
          geomType: 'Polygon',
          mapSources: [
            MAP_SOURCES.wms_alro
          ],
          //image: '/static/images/legend-polygon.svg',
          style: {
            border: '1px solid #A04000',
            background: 'rgba(160,64,0,0.1)'
          }
      },{
          title: 'เขตป่า',
          type: 'checkbox',
          checked: false,
          visible: false,
          geomType: 'Polygon',
          image: '/static/images/legend-polygon.svg',
          mapSources: [
            MAP_SOURCES.wms_mangrove_forest,
            MAP_SOURCES.wms_national_forest,
            MAP_SOURCES.wms_national_park,
            MAP_SOURCES.wms_forest_park,
            MAP_SOURCES.wms_wildlife_sanctuary,
            MAP_SOURCES.wms_non_hunting
          ],
          legend: [{
            title: 'เขตรักษาพันธุ์สัตว์ป่า',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [
              MAP_SOURCES.wms_wildlife_sanctuary
              ],
            style: {
              border: '1px solid #99A3A4',
              background: 'rgba(153,163,164,0.8)'                                                   
            }
            },{
              title: 'เขตห้ามล่าสัตว์ป่า',
              type: 'checkbox',
              checked: false,
              visible: false,
              geomType: 'Polygon',
              mapSources: [
                MAP_SOURCES.wms_non_hunting
                ],
              style: {
                border: '1px solid #E74C3C',
                background: 'rgba(231,76,60,0.8)'                                                   
              }
            },{
              title: 'อุทยานแห่งชาติ',
              type: 'checkbox',
              checked: false,
              visible: false,
              geomType: 'Polygon',
              mapSources: [
                MAP_SOURCES.wms_national_park
                ],
              style: {
                border: '1px solid #2ECC71',
                background: 'rgba(46,204,113,0.8)'                                                   
              }
            },{
              title: 'วนอุทยาน',
              type: 'checkbox',
              checked: false,
              visible: false,
              geomType: 'Polygon',
              mapSources: [
                MAP_SOURCES.wms_forest_park
                ],
              style: {
                border: '1px solid #D6F09E',
                background: 'rgba(214,240,158,0.8)'                                                   
              }
            },{
              title: 'ป่าสงวนแห่งชาติ',
              type: 'checkbox',
              checked: false,
              visible: false,
              geomType: 'Polygon',
              mapSources: [
                MAP_SOURCES.wms_national_forest
                ],
              style: {
                border: '1px solid #239B56',
                background: 'rgba(35,155,86,0.8)'                                                   
              }
            },{
              title: 'ป่าชายเลน',
              type: 'checkbox',
              checked: false,
              visible: false,
              geomType: 'Polygon',
              mapSources: [
                MAP_SOURCES.wms_mangrove_forest
                ],
              style: {
                border: '1px solid #F7DC6F',
                background: 'rgba(247,220,111,0.8)'
              }                                                   
            }]
      },{
        title: 'พื้นที่ชลประทาน', //'แหล่งน้ำและชลประทาน',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Polygon',
        mapSources: [{
          service: 'WMS',
          serverType: 'geoserver',
          params: {
            'LAYERS': 'gisagro:irrigation_project',
            'TILED': true
          },
          useGwc: true, 
          visible: true, 
        }],
        //image: '/static/images/legend-line-polygon.svg',
        style: {
          border: '2px solid #0000ff',
          background: 'rgba(145, 194, 255, 0.1)'
        }
        /*,legend: [{
            title: 'พื้นที่ชลประทาน',
            style: {
              background:'#91c2ff'
            }
          },{
            title: 'แหล่งน้ำ',
            style: {
              background:'#2082ff'
            }
          },{
            title: 'คลองส่งน้ำ',
            style:{
              height:'2px',
              background: '#2082ff'
            }
          }]*/
      },{
        title: 'แหล่งน้ำขนาดเล็ก',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Polygon',
        mapSources: [{
            service: 'WMS',
            serverType: 'geoserver',
            url: 'https://water-s03.gistda.or.th/geoserver/SmallWaterArea/wms',
            params: {
              'LAYERS': 'SmallWaterArea:SmallWaterVallaris', 
              'TILED': true
            },
            //useGwc: true,
            visible: false
          }],
        image: '/static/images/legend-polygon-range-blue.svg',
        legend: [{
            title: 'น้อยกว่า 40%',
            className: 'point-in-polygon water-lt40',
            style: {
              background:'#89b193'
            }
          },{
            title: '40 - 90%',
            className: 'point-in-polygon water-4090',
            style: {
              background:'#ffff85'
            }
          },{
            title: 'มากกว่า 90%',
            className: 'point-in-polygon water_gt90',
            style: {
              background:'#ec8d8d'
            }
          }]
      },{
        title: 'ชนิดของดิน',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Polygon',
        mapSources: [{
            service: 'WMS',
            serverType: 'geoserver',
            params: {
              'LAYERS': 'gisagro:soil_class', 
              'TILED': true
            },
            useGwc: true, 
            visible: false
          }],
        image: '/static/images/legend-polygon.svg',
        legend: [{
            title: 'ดินในพื้นที่ความลาดชันสูง',
            style: {
              background:'#ff2200'
            }
          },{
            title: 'ดินในพื้นที่ดอนในเขตดินแห้ง',
            style: {
              background:'#ff9900'
            }
          }, {
            title: 'ดินในพื้นที่ดอนในเขตดินชื้น',
            style: {
              background:'#cddc39'
            }
          },{
            title: 'ดินในพื้นที่ลุ่มหรือพื้นที่น้ำขัง',
            style: {
              background:'#00bcd4'
            }
          }]
      }/*,{
        title: 'ความลาดชัน',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Polygon',
        mapSources: [],
        image: '/static/images/legend-polygon.svg',
        legend: [{
            title: '< 10°;',
            style: {
              background:'#7aab00'
            }
          },{
            title: '10° - 20°;',
            style: {
              background:'#ffff00'
            }
          },{
            title: '20° - 35°;',
            style: {
              background:'#ff9900'
            }
          },{
            title: '> 35°;',
            style: {
              background:'#ff2200'
            }
          }]
      }*/]
  },{
    header: 'ข้อมูลด้านภัยธรรมชาติ',
    children: [{
        title: 'ข้อมูลจุดความร้อน (Hotspot)',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Point',
        mapSources: [{
          service: 'WMTS',
          wmtsMatrixSet: 'EPSG:900913',
          wmtsEPSG: 'EPSG:900913',
          url: 'http://tile.gistda.or.th/geoserver/gwc/service/wmts/',
          serverType: 'geoserver',
          featureType: 'forestfire:hotspot_datenow',
          visible: false
        }],
        className: 'legend-point',
        image: '/static/images/legend-polygon.svg',
        legend: [{
            title: 'ป่าอนุรักษ์',
            className: 'circle16',
            style: {
              background: '#00e400'
            }
          },{
            title: 'ป่าสงวนแห่งชาติ',
            className: 'circle16',
            style: {
              broder: '1px solid #159e08',
              background: '#159e08'
            }
          },{
            title: 'เขตสปก.',
            className: 'circle16',
            style: {
              broder: '1px solid #f555f7',
              background: '#f555f7'
            }
          },{
            title: 'พื้นที่เกษตร',
            className: 'circle16',
            style: {
              broder: '1px solid #f4fc00',
              background: '#f4fc00'
            }
          },{
            title: 'พื้นที่ริมทางหลวง (250 เมตร)',
            className: 'circle16',
            style: {
              broder: '1px solid #9f4900',
              background: '#9f4900'
            }
          },{
            title: 'ชุมชนและอื่นๆ',
            className: 'circle16',
            style: {
              broder: '1px solid #c2c3bd',
              background: '#c2c3bd'
            }
          },{
            title: 'จุดความร้อนนอกประเทศ',
            className: 'circle16',
            style: {
              broder: '1px solid #fcad2a',
              background: '#fcad2a'
            }
          }]
      },{
        title: 'NDVI ราย 7 วัน',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Polygon',
        mapSources: [{
          service: 'WMTS',
          wmtsMatrixSet: 'EPSG:3857',
          wmtsEPSG: 'EPSG:3857',
          maxZoomLevel: 20,
          url: 'http://tile.gistda.or.th/geoserver/gwc/service/wmts/',
          serverType: 'geoserver',
          featureType: 'drought:ndvi7day_thai',
          visible: false,
          opacity: 0.95
        }],
        image: '/static/images/legend-polygon.svg',
        legend: [{
            title: 'พืชพรรณมีความหนาแน่นสูง',
            style: {
              background: '#275806'
            }
          },{
            title: 'พืชพรรณมีความหนาแน่นปานกลาง',
            style: {
              background: '#e34d01'
            }
          },{
            title: 'พืชพรรณมีความหนาแน่นน้อย',
            style: {
              background: '#fffb00'
            }
          },{
            title: 'แหล่งน้ำ',
            style: {
              background: '#005aef'
            }
          }]
      },{
        title: 'อุทกภัย',
        image: '/static/images/legend-polygon-range-blue.svg',
        children: [{
            title: 'พื้นที่น้ำท่วมรอบ 7 วัน',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: "Polygon",
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              url: 'https://gisagro.gistda.or.th/rest/proxy/?url=http://tile.gistda.or.th/geoserver/wms',
              params: {
                LAYERS: 'flood:floodarea_tambon', 
                //'LAYERS':'gisagro:floodarea_tambon_in_7days',
                TILED: true
              },
              visible: false,
              useGwc: true
              //service: 'WMS', //'WMTS',
              //wmtsMatrixSet: 'EPSG:900913',
              //wmtsEPSG: 'EPSG:900913',
              //url: 'http://tile.gistda.or.th/geoserver/gwc/service/wmts/',
              //featureType: 'flood:floodarea_tambon', //'flood:flood_update',
              //visible: false //display it after added to map
            }],
            style: {
              background:'rgba(46,100,254,0.6)'
            }
          },{
            title: 'พื้นที่น้ำท่วมในปี',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: "Polygon",
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              params: {
                'LAYERS': 'gisagro:flood_area_2016', 
                'TILED': true
              },
              visible: false,
              useGwc: true
            }],
            style: {
              background:'rgba(46,100,254,0.6)'
            },
            actions: [{
              do: function(selection) {
                let ms = this.mapSources[0];
                if (ms.nativeLayer) {
                  let source = ms.nativeLayer.getSource();
                  source.updateParams({
                    LAYERS: 'gisagro:flood_area_' + selection.value,
                    ts: Date.now()
                  });
                  if (ms.visible) {
                    source.refresh();
                  }
                }
              },
              type: 'select',
              width: '64px',
              selected: '2016',
              options: [{
                  title: '2559',
                  value: '2016'
                },{
                  title: '2558',
                  value: '2015'
                },{
                  title: '2557',
                  value: '2014'
                },{
                  title: '2556',
                  value: '2013'
                },{
                  title: '2555',
                  value: '2012'
                },{
                  title: '2554',
                  value: '2011'
                },{
                  title: '2553',
                  value: '2010'
                },{
                  title: '2552',
                  value: '2009'
                },{
                  title: '2551',
                  value: '2008'
                },{
                  title: '2550',
                  value: '2007'
                },{
                  title: '2540',
                  value: '2006'
                }]
            }]
            /*
            children: [{
                title: 'พ.ศ. 2559',
                type: 'checkbox',
                checked: false
              },{
                title: 'พ.ศ. 2558',
                type: 'checkbox',
                checked: false
              },{
                title: 'พ.ศ. 2557',
                type: 'checkbox',
                checked: false
              },{
                title: 'พ.ศ. 2556',
                type: 'checkbox',
                checked: false
              },{
                title: 'พ.ศ. 2555',
                type: 'checkbox',
                checked: false
              },{
                title: 'ปีอื่นๆ',
                actions: [{
                  action: 'getMoreFloodYears',
                  icon: 'more_vert'
                }]
              }]*/
          },{
            title: 'ความถี่น้ำท่วมขังในรอบ 11 ปี',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: "Polygon",
            mapSources: [{
              service: 'WMTS',
              maxZoomLevel: 30,
              wmtsMatrixSet: 'EPSG:900913',
              wmtsEPSG: 'EPSG:900913',
              url: 'http://tile.gistda.or.th/geoserver/gwc/service/wmts/',
              serverType: 'geoserver',
              featureType: 'flood:flood_freq_2005_2015_box',
              visible: false,
              opacity: 0.8
            }],
            image: '/static/images/legend-polygon-range-blue.svg',
            legend: [{
                title: '',
                className: 'flood-gradient',
                style: {
                  'min-width': '190px',
                  'max-width': '190px',
                  'background-color': 'green', // For browsers that do not support gradients
                  'background-image': 'linear-gradient(90deg, #021efd,#021efd, #fffe00,#fffe00,#fffe00, #ab00e5,#ab00e5,#ab00e5, #fe0000,#fe0000)'
                }
              },{
                title: '<div style="width:210px;margin-top:-2px;font-size:0.8em;display:flex;justify-content:space-between"><span>1 ครั้ง</span><span>2-4 ครั้ง</span><span>5-7 ครั้ง</span><span>8-10 ครั้ง</span></div>',
                className: 'html', //reserved to show the title using v-html
                style: {
                  width: '0px',
                  display: 'none'
                }
              }]
          },{
            title: 'น้ำท่วมซ้ำซากปี 2549-59',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              params: {
                'LAYERS': 'gisagro:flood_risk_level', 
                'TILED': true
              },
              useGwc: true,
              visible: false
            }],
            image: '/static/images/legend-polygon-range-blue.svg',
            legend: [{
                title: "น้ำท่วมซ้ำซาก 3 ครั้งขึ้นไป",
                style: {
                  background:'#0000FF'
                }
              },{
                title: "น้ำท่วมซ้ำซาก 2 ครั้ง",
                style: {
                  background:'#2E64FE'
                }
              },{
                title: "น้ำท่วม 1 ครั้ง",
                style: {
                  background:'#96FFFF'
                }
              },{
                title: "ไม่เคยน้ำท่วม",
                style: {
                  background:'#ffffb2'
                }
              }]
          }]
      },{
        title: 'ภัยแล้ง',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Polygon',
        mapSources: [{
            service: 'WMS',
            serverType: 'geoserver',
            url: 'https://mapcmet.gistda.or.th/geoserver/CropMet/wms',
            params: {
              'LAYERS': 'DroughtHazard', 
              'TILED': true
            },
            //useGwc: true,
            visible: false
          }],
        image: '/static/images/legend-polygon.svg',
        legend: [{
            title: 'แล้ง 5 ครั้งใน 9 ปี',
            style: {
              background:'#e6928e'
            }
          },{
            title: 'แล้ง 4 ครั้งใน 9 ปี',
            style: {
              background:'#f4b19c'
            }
          },{
            title: 'แล้ง 3 ครั้งใน 9 ปี',
            style: {
              background:'#f9d1ab'
            }
          },{
            title: 'แล้ง 2 ครั้งใน 9 ปี',
            style: {
              background:'#f8ecc9'
            }
          },{
            title: 'แล้ง 1 ครั้งใน 9 ปี',
            style: {
              background:'#e7f2c0'
            }
          }]
      },{
        id: 'diseasesandpests', //used for selection to be shown in diseases.chart.vue
        title: 'สถานการณ์โรคและแมลงศัตรูพืช',
        image: '/static/images/legend-polygon.svg',
        actions: [{
          action: 'showDiseaseCharts',
          type: 'button',
          width: 'auto',
          icon: 'mdi-chart-bar'
        }],
        children: [{
            id: '1',
            title: 'ข้าว : เพลี้ยกระโดดสีน้ำตาล',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              url: 'https://mapcmet.gistda.or.th/geoserver/CropMet/wms',
              params: {
                'LAYERS': 'DiseasesMap', 
                'VIEWPARAMS':'DiseaseID:1;Date:' + TODAY_ISO,
                'TILED': true
              },
              //useGwc: true,
              visible: false
            }],
            className: 'point-in-polygon rice',
            style: {
              background:'#ade89d'
            }
          },{
            id: '2',
            title: 'เพลี้ยแป้งมันสำปะหลังสีชมพู',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              url: 'https://mapcmet.gistda.or.th/geoserver/CropMet/wms',
              params: {
                'LAYERS': 'DiseasesMap', 
                'VIEWPARAMS':'DiseaseID:2;Date:' + TODAY_ISO,
                'TILED': true
              },
              //useGwc: true,
              visible: false
            }],
            className: 'point-in-polygon cassava',
            style: {
              background:'#e3b9f3'
            }
          },{
            id: '3',
            title: 'หนอนเจาะลำต้นข้าวโพด',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              url: 'https://mapcmet.gistda.or.th/geoserver/CropMet/wms',
              params: {
                'LAYERS': 'DiseasesMap', 
                'VIEWPARAMS':'DiseaseID:3;Date:' + TODAY_ISO,
                'TILED': true
              },
              //useGwc: true,
              visible: false
            }],
            className: 'point-in-polygon maize',
            style: {
              background:'#f9ffa6'
            }
          },{
            id: '4',
            title: 'หนอนกออ้อยลายจุดเล็ก',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              url: 'https://mapcmet.gistda.or.th/geoserver/CropMet/wms',
              params: {
                'LAYERS': 'DiseasesMap', 
                'VIEWPARAMS':'DiseaseID:4;Date:' + TODAY_ISO,
                'TILED': true
              },
              //useGwc: true,
              visible: false
            }],
            className: 'point-in-polygon sugarcane',
            style: {
              background:'#ffb2b2'
            }
          },{
            id: '5',
            title: 'ลำใย : โรคยอดใบไหม้และผลเน่า',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              url: 'https://mapcmet.gistda.or.th/geoserver/CropMet/wms',
              params: {
                'LAYERS': 'DiseasesMap', 
                'VIEWPARAMS':'DiseaseID:5;Date:' + TODAY_ISO,
                'TILED': true
              },
              //useGwc: true,
              visible: false
            }],
            className: 'point-in-polygon longan',
            style: {
              background:'#a0f5fe'
            }
          },{
            id: '0',
            title: 'ภาพรวม : โรคพืชทั้งหมด',
            type: 'checkbox',
            checked: false,
            visible: false,
            geomType: 'Polygon',
            mapSources: [{
              service: 'WMS',
              serverType: 'geoserver',
              url: 'https://mapcmet.gistda.or.th/geoserver/CropMet/wms',
              params: {
                'LAYERS': 'AllDiseasesMap', 
                'VIEWPARAMS':'Date:' + TODAY_ISO,
                'TILED': true
              },
              //useGwc: true,
              visible: false
            }],
            image: '/static/images/legend-polygon.svg',
          }]
      }/*,{
        title: 'แผ่นดินถล่ม',
        type: 'checkbox',
        checked: false,
        visible: false,
        geomType: 'Polygon',
        mapSources: [],
        image: '/static/images/legend-polygon.svg',
        legend: [{
            title: 'พื้นที่เสี่ยงภัย',
            style: {
              background:'#FFFF66'
            }
          },{
            title: 'พื้นที่ดินถล่มปัจจุบัน',
            style: {
              background:'#FF3333'
            }
          }]
      }*/]
  }];

export default MAP_LAYERS;
